import { Link } from "react-router-dom";
import { BookOpen, FileText, LayoutGrid, ClipboardList, Lock, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow, SectionHead } from "../components/Motion";

const RESOURCE_CARDS = [
  {
    icon: BookOpen,
    title: "Training Manuals & Guides",
    meta: "Field manuals · Study guides",
    body: "Instructor-grade manuals covering defensive operations, adversary tradecraft and zero trust implementation — the same materials used inside Academy classrooms.",
  },
  {
    icon: FileText,
    title: "Research Whitepapers",
    meta: "Doctrine · Threat studies",
    body: "Peer-reviewed publications from the Academy research directorate on emerging adversary behavior, framework gaps and workforce competency standards.",
  },
  {
    icon: LayoutGrid,
    title: "Federal Framework Toolkit",
    meta: "NIST 800-53 · CMMC · FISMA",
    body: "Control-family mappings, implementation templates and audit preparation checklists aligned to the directives that govern national security systems.",
  },
  {
    icon: ClipboardList,
    title: "Workforce Self-Assessment",
    meta: "Competency diagnostic",
    body: "A structured diagnostic mapping your experience against the federal cyber workforce framework, with a recommended Academy pathway in your results.",
  },
];

const NIST_FAMILIES = [
  ["AC", "Access Control"], ["AT", "Awareness & Training"], ["AU", "Audit & Accountability"],
  ["CA", "Assessment & Authorization"], ["CM", "Configuration Management"], ["IA", "Identification & Authentication"],
  ["IR", "Incident Response"], ["MA", "Maintenance"], ["MP", "Media Protection"],
  ["PE", "Physical Protection"], ["PS", "Personnel Security"], ["PT", "PII Processing"],
  ["RA", "Risk Assessment"], ["SA", "System & Services Acquisition"], ["SC", "System & Communications"],
  ["SI", "System & Information Integrity"],
];

const Resources = () => (
  <div data-testid="resources-page">
    <section className="relative bg-navy-950 grid-lines overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-20 lg:pt-52 lg:pb-24">
        <MaskedLine as="div" delay={0.3} className="mb-6">
          <Eyebrow light>Library & Toolkit</Eyebrow>
        </MaskedLine>
        <h1 className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl" data-testid="resources-headline">
          <MaskedLine delay={0.45}>Doctrine, tools and</MaskedLine>
          <MaskedLine delay={0.57}>
            <em className="text-gold-300 italic">the national standard.</em>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.7} className="mt-8 max-w-2xl">
          <p className="text-base sm:text-lg leading-relaxed text-slate-300">
            The Academy's public library — training manuals, research,
            framework mappings and diagnostics. Restricted materials require
            verified credentials through your agency security office.
          </p>
        </MaskedLine>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="resource-cards-section">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {RESOURCE_CARDS.map((r, i) => (
          <Reveal key={r.title} delay={0.05 * i} className="h-full">
            <div className="group h-full bg-white border border-paper-border p-8 lg:p-10 flex flex-col transition-all duration-300 hover:border-gold-500/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-gold-500/10">
              <div className="flex items-start justify-between">
                <r.icon className="w-8 h-8 text-gold-600" strokeWidth={1.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{r.meta}</span>
              </div>
              <h2 className="mt-6 font-serif font-semibold text-xl lg:text-2xl text-navy-900">{r.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 flex-1">{r.body}</p>
              <Link
                to="/contact"
                data-testid={`resource-access-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-gold-600 transition-colors duration-200 hover:text-navy-900 w-fit"
              >
                Request access
                <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-12 flex items-start gap-4 bg-navy-950 border border-gold-500/25 p-6 lg:p-8" data-testid="restricted-access-banner">
          <Lock className="w-5 h-5 text-gold-400 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-serif font-semibold text-white">Restricted Materials Notice</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Classified and need-to-know courseware is released only through
              verified .gov and .mil sponsorship. Contact your agency security
              office to establish eligibility before requesting access.
            </p>
          </div>
        </div>
      </Reveal>
    </section>

    <section className="bg-paper-100 border-t border-paper-border" data-testid="nist-matrix-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHead
          eyebrow="NIST SP 800-53 Rev. 5"
          title="Control-family coverage across the curriculum."
          lede="Every Academy course maps its outcomes to one or more control families. This matrix shows where each family is taught."
        />
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {NIST_FAMILIES.map(([code, name], i) => (
            <Reveal key={code} delay={0.02 * (i % 8)} className="h-full">
              <div
                data-testid={`nist-family-${code.toLowerCase()}`}
                className="h-full bg-white border border-paper-border px-5 py-4 flex items-center gap-4 transition-all duration-300 hover:border-gold-500/60"
              >
                <span className="font-mono font-semibold text-sm text-gold-600 bg-navy-900 px-2.5 py-1.5">
                  {code}
                </span>
                <span className="text-[13px] font-medium text-navy-900 leading-snug">{name}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default Resources;
